/**
 * V14.5 Part 16 — Global client-error reporting.
 *
 * Captures errors the React ErrorBoundary can't see:
 *   • window.onerror — sync errors in event handlers, image onloads, etc.
 *   • window.unhandledrejection — promise rejections (axios timeouts,
 *     failed lazy-chunk imports, fetch errors).
 *   • ChunkLoadError — when a code-split bundle fails to load (most
 *     common cause of post-OTP blank screen after a redeploy: the user's
 *     SPA still has the OLD index.html which points to NEW bundle paths
 *     that 404 after Cloudflare cache expires).
 *
 * Posts to `POST /api/client-errors` (best-effort, never blocks UX).
 * Auth header attached if a token is present so we know which user.
 *
 * Auto-recovery: ChunkLoadError triggers a one-time hard reload so the
 * user gets the fresh index.html → fresh chunk URLs → recovery without
 * any manual intervention.
 */

let installed = false;
let chunkReloadOnce = false;

function postClientError(payload) {
  try {
    const backend = process.env.REACT_APP_BACKEND_URL;
    if (!backend) return;
    const token = localStorage.getItem('ss_token');
    fetch(`${backend}/api/client-errors`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      keepalive: true,
      body: JSON.stringify({
        ...payload,
        url: window.location.href,
        user_agent: navigator.userAgent,
        ts: new Date().toISOString(),
      }),
    }).catch(() => { /* swallow */ });
  } catch { /* never crash on logging */ }
}

function looksLikeChunkLoadError(err) {
  const msg = String((err && err.message) || err || '');
  const name = String((err && err.name) || '');
  return (
    name === 'ChunkLoadError' ||
    msg.includes('Loading chunk ') ||
    msg.includes('Loading CSS chunk ') ||
    msg.includes('Failed to fetch dynamically imported module')
  );
}

function maybeAutoReloadOnChunkError(err) {
  if (!looksLikeChunkLoadError(err) || chunkReloadOnce) return;
  // Use sessionStorage to make this survive at most one reload — never loop.
  if (sessionStorage.getItem('ss_chunk_reload_attempted') === '1') return;
  sessionStorage.setItem('ss_chunk_reload_attempted', '1');
  chunkReloadOnce = true;
  // One-time hard reload to fetch the fresh index.html and chunk paths.
  setTimeout(() => { try { window.location.reload(); } catch { /* noop */ } }, 150);
}

export function installGlobalErrorReporting() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event) => {
    const err = event?.error || event?.message;
    postClientError({
      kind: 'window_error',
      message: String((err && err.message) || err || event?.message || 'unknown'),
      stack: String((err && err.stack) || '').slice(0, 4000),
      filename: event?.filename || '',
      lineno: event?.lineno || 0,
      colno: event?.colno || 0,
    });
    maybeAutoReloadOnChunkError(err);
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event?.reason;
    postClientError({
      kind: 'unhandled_rejection',
      message: String((reason && reason.message) || reason || 'unknown'),
      stack: String((reason && reason.stack) || '').slice(0, 4000),
    });
    maybeAutoReloadOnChunkError(reason);
  });

  // Reset the chunk-reload guard once the new page successfully renders.
  // (If we DON'T reach this line, the user is in a real loop and we want
  // the guard to stay so we don't keep reloading.)
  setTimeout(() => sessionStorage.removeItem('ss_chunk_reload_attempted'), 5000);
}
